<script setup lang="ts">
import {
  ref, defineProps, defineEmits, watch, computed,
} from 'vue';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '@/config/firebase';

export interface Service {
  id: string;
  docId: string;
  title: string;
  subTitle: string;
  category: string;
  description: string;
  coverImg: string;
  images: string[];
  config?: any;
}

type ServiceInputFieldOption = { label: string; image?: string };
type ServiceInputFieldType =
  | 'text'
  | 'dropdown'
  | 'multiselect'
  | 'datetime'
  | 'attachment'
  | 'carousel-select'
  | 'counter';
type ServiceInputField = {
  id: string;
  label: string;
  type: ServiceInputFieldType;
  placeholder?: string;
  // In saved config:
  // - dropdown/multiselect: string[]
  // - carousel-select: {label,image}[]
  options: string[] | ServiceInputFieldOption[] | null;
  required: boolean;
  icon?: string;
  min?: number;
  max?: number;
};
type ServicePriceRule = { if: Record<string, string>; add: number };
type ServiceConfig = {
  isPaymentFirst: boolean;
  fields: ServiceInputField[];
  price: {
    base: number;
    rules: ServicePriceRule[];
  };
};

const props = defineProps<{ service: Service }>();
const emit = defineEmits(['close', 'updated']);

const form = ref<Service>({ ...props.service });

const newCoverImgFile = ref<File | null>(null);
const newImageFiles = ref<File[]>([]);

const newCoverPreview = computed(() => (newCoverImgFile.value ? URL.createObjectURL(newCoverImgFile.value) : null));
const newImagesPreview = computed(() => newImageFiles.value.map((file) => URL.createObjectURL(file)));

const inputConfig = ref<ServiceConfig>(
  (props.service.config as ServiceConfig) || {
    isPaymentFirst: true,
    fields: [],
    price: {
      base: 0,
      rules: [],
    },
  },
);
console.log(JSON.stringify(inputConfig.value, null, 2));

type NewFieldState = {
  id: string;
  label: string;
  type: ServiceInputFieldType;
  placeholder: string;
  options: string | ServiceInputFieldOption[];
  required: boolean;
  icon: string;
  min: number;
  max: number;
};
const newField = ref<NewFieldState>({
  id: '',
  label: '',
  type: 'text',
  placeholder: '',
  options: '',
  required: false,
  icon: '',
  min: 0,
  max: 10,
});

const newRule = ref({
  conditions: [{ key: '', value: '' }],
  add: 0,
});

// JSON editor mode for config
const viewMode = ref<'builder' | 'json'>('builder');
const jsonConfig = ref<string>(JSON.stringify(inputConfig.value, null, 2));
const jsonError = ref<string | null>(null);
const jsonSuccess = ref<string | null>(null);

watch(viewMode, (mode) => {
  if (mode === 'json') {
    jsonConfig.value = JSON.stringify(inputConfig.value, null, 2);
    jsonError.value = null;
    jsonSuccess.value = null;
  }
});

function applyJsonConfig() {
  try {
    const parsed = JSON.parse(jsonConfig.value);
    inputConfig.value = parsed as ServiceConfig;
    jsonError.value = null;
    jsonSuccess.value = 'JSON applied to configuration.';
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    jsonError.value = `Invalid JSON: ${message}`;
    jsonSuccess.value = null;
  }
}

// Keep newField.options shape consistent with selected type
watch(
  () => newField.value.type,
  (t) => {
    if (t === 'carousel-select') {
      if (!Array.isArray(newField.value.options)) newField.value.options = [];
    } else if (Array.isArray(newField.value.options)) {
      newField.value.options = '';
    }
  },
);

function carouselOptions(): ServiceInputFieldOption[] {
  return Array.isArray(newField.value.options)
    ? (newField.value.options as ServiceInputFieldOption[])
    : [];
}

// Helper
function resizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      const aspectRatio = img.width / img.height;
      canvas.width = Math.min(img.width, 1250);
      canvas.height = canvas.width / aspectRatio;

      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Canvas to Blob failed'));
          }
        }, file.type);
      } else {
        reject(new Error('No canvas context'));
      }
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function uploadFile(file: File): Promise<string> {
  const resized = await resizeImage(file);
  const fileRef = storageRef(storage, `services/${Date.now()}-${file.name}`);
  const snapshot = await uploadBytes(fileRef, resized);
  return getDownloadURL(snapshot.ref);
}

async function updateService() {
  try {
    const updates: Partial<Service> = {};

    // Replace cover image
    if (newCoverImgFile.value) {
      const isInImages = props.service.images.includes(props.service.coverImg);
      if (!isInImages) {
        await deleteObject(storageRef(storage, props.service.coverImg));
      }
      updates.coverImg = await uploadFile(newCoverImgFile.value);
    }

    // Replace additional images
    if (newImageFiles.value.length > 0) {
      const coverToKeep = props.service.coverImg;

      // delete only images that are NOT the cover image
      // eslint-disable-next-line no-restricted-syntax
      for (const url of props.service.images) {
        if (url !== coverToKeep) {
          // eslint-disable-next-line no-await-in-loop
          await deleteObject(storageRef(storage, url));
        }
      }

      const newUrls = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const file of newImageFiles.value) {
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadFile(file);
        newUrls.push(url);
      }
      updates.images = newUrls;
    }
    console.log(inputConfig.value);
    // Other fields
    updates.config = inputConfig.value;
    updates.title = form.value.title;
    updates.subTitle = form.value.subTitle;
    updates.category = form.value.category;
    updates.description = form.value.description;

    await updateDoc(doc(db, 'services', props.service.docId), updates);
    alert('Service updated successfully!');
    emit('updated');
    emit('close');
  } catch (error) {
    console.error('Update failed:', error);
    alert('Update failed');
  }
}

function onFileChange(event: Event, type: string) {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;
  if (type === 'coverImg') {
    // eslint-disable-next-line prefer-destructuring
    newCoverImgFile.value = target.files[0];
  } else if (type === 'images') {
    newImageFiles.value = Array.from(target.files);
  }
}

function moveField(index: number, direction: 'up' | 'down') {
  const fields = inputConfig.value.fields;
  if (
    (direction === 'up' && index === 0)
    || (direction === 'down' && index === fields.length - 1)
  ) return;

  const swapIndex = direction === 'up' ? index - 1 : index + 1;
  const temp = fields[swapIndex];
  fields[swapIndex] = fields[index];
  fields[index] = temp;
}

async function onCarouselImageUpload(event: Event, index: number) {
  const input = event.target as HTMLInputElement;
  if (!input.files?.[0]) return;

  const file = input.files[0];
  const url = await uploadFile(file);
  if (!Array.isArray(newField.value.options)) {
    newField.value.options = [];
  }
  (newField.value.options as ServiceInputFieldOption[])[index].image = url;
}
</script>

<template>
  <div
    class="fixed inset-0 flex justify-center items-center bg-black bg-opacity-30 z-50"
  >
    <div
      class="bg-white dark:bg-gray-800 w-[40vw] max-h-[90vh] p-6 rounded-lg overflow-y-auto"
    >
      <h2 class="text-xl font-bold mb-4">Edit Service</h2>

      <label class="block mb-2 font-medium">Title</label>
      <input v-model="form.title" class="input" />

      <label class="block mt-4 mb-2 font-medium">SubTitle</label>
      <input v-model="form.subTitle" class="input" />

      <label class="block mt-4 mb-2 font-medium">Category</label>
      <input v-model="form.category" class="input" />

      <label class="block mt-4 mb-2 font-medium">Description</label>
      <textarea v-model="form.description" class="input" rows="4"></textarea>

      <div class="mt-6">
        <label class="font-medium mb-2 block">Replace Cover Image</label>
        <input type="file" @change="onFileChange($event, 'coverImg')" />
        <div v-if="newCoverPreview || form.coverImg" class="mt-2">
          <img
            :src="newCoverPreview || form.coverImg"
            class="w-60 rounded"
            alt="TALAB service"
          />
        </div>
      </div>

      <div class="mt-6">
        <label class="font-medium mb-2 block">Replace Additional Images</label>
        <input type="file" multiple @change="onFileChange($event, 'images')" />
        <div class="flex mt-2 gap-2 overflow-auto">
          <img
            v-for="(img, i) in newImagesPreview.length
              ? newImagesPreview
              : form.images"
            :key="i"
            :src="img"
            class="w-32 h-20 object-cover rounded"
            alt="TALAB service"
          />
        </div>
      </div>

      <hr class="my-6 border-gray-400" />
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold">Service Form Configuration</h3>
        <div class="flex gap-2">
          <button
            @click="viewMode = 'builder'"
            :class="['px-3 py-1 rounded text-sm', viewMode === 'builder' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 dark:text-white']"
          >
            Builder
          </button>
          <button
            @click="viewMode = 'json'"
            :class="['px-3 py-1 rounded text-sm', viewMode === 'json' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 dark:text-white']"
          >
            JSON
          </button>
        </div>
      </div>

      <!-- JSON Mode -->
      <div v-if="viewMode === 'json'" class="mb-4">
        <textarea
          v-model="jsonConfig"
          class="input font-mono min-h-[260px]"
          spellcheck="false"
        ></textarea>
        <div v-if="jsonError" class="text-red-500 text-sm mt-2">{{ jsonError }}</div>
        <div v-if="jsonSuccess" class="text-green-600 text-sm mt-2">{{ jsonSuccess }}</div>
        <div class="flex justify-end mt-3">
          <button class="bg-indigo-600 text-white px-3 py-1 rounded" @click="applyJsonConfig">
            Apply JSON
          </button>
        </div>
      </div>

      <template v-if="viewMode === 'builder'">
        <div class="flex items-center gap-2 mb-4">
          <label class="font-medium">Show Payment Button:</label>
          <input type="checkbox" v-model="inputConfig.isPaymentFirst" />
        </div>

        <!-- Add Field -->
        <div class="border border-gray-300 rounded p-4 mb-4">
          <h4 class="font-semibold mb-2">Add New Input Field</h4>
          <input
            v-model="newField.id"
            placeholder="ID (e.g., documentType)"
            class="input mb-2"
          />
          <input
            v-model="newField.label"
            placeholder="Label"
            class="input mb-2"
          />
          <input
            v-if="newField.type === 'text'"
            v-model="newField.placeholder"
            placeholder="Placeholder"
            class="input mb-2"
          />
          <select v-model="newField.type" class="input mb-2">
            <option value="text">Text Input</option>
            <option value="dropdown">Dropdown</option>
            <option value="multiselect">Multi-Select</option>
            <option value="datetime">Date/Time Picker</option>
            <option value="attachment">Attachment</option>
            <option value="carousel-select">Carousel Select</option>
            <option value="counter">Counter</option>
          </select>

          <input
            v-if="newField.type === 'dropdown' || newField.type === 'multiselect'"
            v-model="newField.options"
            placeholder="Options (comma separated)"
            class="input mb-2"
          />
          <div
            v-if="newField.type === 'carousel-select'"
            class="bg-gray-100 dark:bg-gray-700 p-3 rounded mt-2"
          >
            <h5 class="font-medium text-sm mb-2">Carousel Options</h5>
            <div
            v-for="(opt, i) in carouselOptions()"
              :key="i"
              class="flex items-center mb-2 gap-2"
            >
              <img
              :src="opt.image"
                class="w-12 h-12 object-cover rounded"
              v-if="opt.image"
                alt="Image"
              />
              <input
              v-model="opt.label"
                placeholder="Label"
                class="input flex-1"
              />
              <input
                type="file"
                accept="image/*"
                @change="onCarouselImageUpload($event, i)"
                class="input max-w-[140px]"
              />
              <button
              @click="(newField.options as ServiceInputFieldOption[]).splice(i, 1)"
                class="text-red-500 hover:text-red-700 text-sm"
              >
                ✕
              </button>
            </div>
            <button
              class="text-sm text-[#C59C2F] mt-2"
            @click="(newField.options as ServiceInputFieldOption[]).push({ label: '', image: '' })"
            >
              + Add Option
            </button>
          </div>
          <div v-if="newField.type === 'counter'" class="space-y-2">
            <input
              v-model="newField.icon"
              placeholder="Ionicon name (e.g., person)"
              class="input"
            />
            <input
              type="number"
              v-model.number="newField.min"
              placeholder="Min value"
              class="input"
            />
            <input
              type="number"
              v-model.number="newField.max"
              placeholder="Max value"
              class="input"
            />
          </div>
          <label class="block mb-2">
            <input type="checkbox" v-model="newField.required" class="mr-2" />
            Required Field
          </label>
          <button
            @click="
              () => {
                console.log(newField.type);
                inputConfig.fields.push({
                  ...newField,
                  options: newField.options.length
                    ? newField.type === 'carousel-select'
                    ? (newField.options as ServiceInputFieldOption[])
                    : (newField.options as string).split(',').map((s: string) => s.trim())
                    : null,
                });
                newField.id = '';
                newField.label = '';
              (newField.placeholder = ''), (newField.options = '');
                newField.type = 'text';
                newField.required = false;
                newField.icon = '';
              }
            "
            class="bg-green-600 text-white px-3 py-1 rounded"
          >
            Add Field
          </button>
        </div>

        <!-- Fields List -->
        <div v-if="inputConfig?.fields?.length" class="mb-6">
          <h4 class="font-semibold mb-2">Current Fields:</h4>
          <ul class="space-y-2">
            <li
              v-for="(field, i) in inputConfig.fields"
              :key="i"
              class="flex items-center justify-between bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded"
            >
              <div
                class="flex items-center gap-2 text-sm text-black dark:text-white"
              >
                <span
                  >{{ field.label }}
                  <span class="text-gray-400">({{ field.type }})</span></span
                >
              </div>

              <div class="flex items-center gap-2">
                <button
                  @click="moveField(i, 'up')"
                  :disabled="i === 0"
                  class="text-gray-500 hover:text-[#C59C2F] disabled:opacity-30"
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  @click="moveField(i, 'down')"
                  :disabled="i === inputConfig.fields.length - 1"
                  class="text-gray-500 hover:text-[#C59C2F] disabled:opacity-30"
                  title="Move down"
                >
                  ▼
                </button>
                <button
                  @click="() => inputConfig.fields.splice(i, 1)"
                  class="text-red-500 hover:text-red-700"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            </li>
          </ul>
        </div>

        <!-- Price Rules -->
        <div class="border border-gray-300 rounded p-4" v-if="inputConfig.isPaymentFirst">
          <h4 class="font-semibold mb-2">Pricing Configuration</h4>
          <label class="block mb-2">Base Price</label>
          <input
            type="number"
            v-model="inputConfig.price.base"
            class="input mb-4"
          />

          <div>
            <label class="block mb-1 font-medium">Add Rule:</label>

            <div
              v-for="(cond, idx) in newRule.conditions"
              :key="idx"
              class="flex items-center gap-2 mb-2"
            >
              <input
                v-model="cond.key"
                placeholder="Field ID"
                class="input flex-1"
              />
              <input
                v-model="cond.value"
                placeholder="Expected Value"
                class="input flex-1"
              />
              <button
                v-if="newRule.conditions.length > 1"
                @click="newRule.conditions.splice(idx, 1)"
                class="text-red-500"
              >
                ✕
              </button>
            </div>

            <button
              class="text-sm text-[#C59C2F] mb-3"
              @click="newRule.conditions.push({ key: '', value: '' })"
            >
              + Add Another Condition
            </button>

            <input
              type="number"
              v-model.number="newRule.add"
              placeholder="Price Add"
              class="input mb-2"
            />

            <button
              class="bg-indigo-600 text-white px-3 py-1 rounded"
              @click="
                () => {
                  const ifObject: Record<string, string> = {};
                  newRule.conditions.forEach((c) => {
                    if (c.key && c.value) {
                      ifObject[c.key] = c.value;
                    }
                  });

                  if (Object.keys(ifObject).length) {
                    inputConfig.price.rules.push({
                      if: ifObject,
                      add: Number(newRule.add),
                    });
                  }

                  newRule.conditions = [{ key: '', value: '' }];
                  newRule.add = 0;
                }
              "
            >
              Add Rule
            </button>
          </div>

          <ul class="mt-4 space-y-2">
            <li
              v-for="(rule, i) in inputConfig.price.rules"
              :key="i"
              class="flex justify-between items-center text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded"
            >
              <span>
                If
                <span v-for="([key, val], j) in Object.entries(rule.if)" :key="key" class="inline-block">
                  <strong>{{ key }}</strong> = "<em>{{ val }}</em
                  >"<span v-if="j < Object.entries(rule.if).length - 1"> & </span>
                </span>
                 Add <strong>£{{ rule.add }}</strong>
              </span>
              <button
                @click="() => inputConfig.price.rules.splice(i, 1)"
                class="text-red-500"
              >
                Remove
              </button>
            </li>
          </ul>
        </div>
      </template>

      <div class="flex justify-end mt-6 gap-3">
        <button @click="emit('close')" class="bg-gray-300 px-4 py-2 rounded">
          Cancel
        </button>
        <button
          @click="updateService"
          class="bg-indigo-600 text-white px-4 py-2 rounded"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input {
  @apply w-full border border-gray-300 rounded px-3 py-2 bg-white dark:bg-gray-700 dark:text-white;
}
</style>
